/**
 * The payroll lock — the boundary between "hours somebody typed" and "hours that were paid".
 *
 * ── Why this screen exists ───────────────────────────────────────────────────
 *
 * `lock-period`, `:id/lock` and `:id/unlock` all shipped with no caller. The consequence is
 * not a missing button: nothing in the product could make a period IMMUTABLE, so hours could
 * be edited after payroll had priced them and after an invoice had gone out. Payroll and
 * billing diverge silently, and the first anyone hears of it is a client query about an
 * invoice that no longer matches the timesheet behind it.
 *
 * ── The three things this screen refuses to hide ─────────────────────────────
 *
 *  1. WHAT WAS SKIPPED. `lock-period` locks approved sheets and REPORTS the rest. A sheet
 *     that is still awaiting approval will not be paid in this run, and the person running
 *     payroll has to see that list while there is still time to chase it — not discover it
 *     when somebody asks where their money is.
 *  2. AN UNPRICEABLE RATE. `overtimeRate` is a union, not a number. When it is not evaluable
 *     the reason names the missing compensation record, and this screen shows that reason
 *     rather than a zero. Paying overtime at a substituted zero is a wage claim.
 *  3. WHERE AN UNLOCK ACTUALLY WENT. `unlock` may take effect directly or route to an
 *     approval workflow, and the response says which. Reporting "unlocked" when the server
 *     said "sent for approval" tells payroll they may edit a sheet that is still locked.
 */
import { useState } from 'react';
import { Lock, LockOpen } from 'lucide-react';

import {
  usePayrollHours,
  useTimesheetLock,
  useTimesheetPeriods,
  type PayrollHoursRow,
  type PayrollTimesheetLine,
} from '@frontend/api/hooks/timesheets';
import { Can } from '@frontend/auth/ProtectedRoute';
import { EntityRefName } from '@frontend/components/shared/EntityRefName';
import { formatDateRange, formatHours } from '@frontend/lib/format';
import { Button } from '@frontend/components/ui/Button';
import { DataTable, type Column } from '@frontend/components/ui/DataTable';
import { FieldShell, Select, Textarea } from '@frontend/components/ui/Field';
import { Modal } from '@frontend/components/ui/Modal';
import { Banner, PageHeader } from '@frontend/components/ui/Page';
import { StatusBadge } from '@frontend/components/ui/StatusBadge';
import { EmptyState, ErrorState, LoadingRows } from '@frontend/components/ui/States';
import { useToast } from '@frontend/components/ui/Toast';

export function PayrollLockPage(): JSX.Element {
  const periods = useTimesheetPeriods(undefined, 8);
  const toast = useToast();
  const { lockPeriod, lockOne, unlockOne } = useTimesheetLock();

  const options = periods.data?.periods ?? [];
  const [selectedIndex, setSelectedIndex] = useState(0);
  const period = options[selectedIndex]?.period ?? null;
  const periodStart = period?.startDate ?? '';
  const periodEnd = period?.endDate ?? '';

  const hours = usePayrollHours(periodStart, periodEnd);
  const [skipped, setSkipped] = useState<{ timesheetId: string; reason?: string }[]>([]);
  const [unlocking, setUnlocking] = useState<PayrollTimesheetLine | null>(null);
  const [reason, setReason] = useState('');

  const rows = hours.data?.rows ?? [];
  const unlockedCount = rows.filter((row) => !row.allLocked).length;

  const columns: Column<PayrollHoursRow>[] = [
    {
      key: 'employee',
      header: 'Employee',
      cell: (row) => <EntityRefName entityType="employees" id={row.employeeId} />,
    },
    { key: 'total', header: 'Total', numeric: true, cell: (row) => formatHours(row.totalHours) },
    { key: 'billable', header: 'Billable', numeric: true, hideOnMobile: true, cell: (row) => formatHours(row.billableHours) },
    {
      key: 'overtime',
      header: 'Overtime',
      numeric: true,
      cell: (row) => {
        if (Number(row.overtimeHours) === 0) return <span className="text-content-tertiary">—</span>;
        // The rate is a union. A missing compensation record must read as "cannot price
        // this", never as a zero that quietly multiplies out to nothing.
        const priceable = row.overtimeRate?.evaluable !== false;
        return priceable ? (
          <span className="font-medium text-warning">{formatHours(row.overtimeHours)}</span>
        ) : (
          <span className="text-error" title={row.overtimeRate?.reason ?? undefined}>
            {formatHours(row.overtimeHours)} — no rate
          </span>
        );
      },
    },
    {
      key: 'state',
      header: 'Sheets',
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.timesheets.map((line) => (
            <StatusBadge key={line.timesheetId} status={line.status} />
          ))}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {row.timesheets.map((line) =>
            line.isLocked ? (
              // `timesheets.unlock` is a separate, sensitive permission and it is NOT in the
              // Payroll Processor set — locking and reopening are deliberately different
              // authorities. Without this gate the person who locks the period sees a
              // Reopen button that 403s, which reads as a broken product rather than as a
              // boundary. An organisation that wants payroll to reopen its own periods adds
              // the permission to a cloned set; that is the documented route.
              <Can key={line.timesheetId} permission="timesheets.unlock">
                <Button
                  size="xs"
                  variant="ghost"
                  leadingIcon={<LockOpen className="h-3.5 w-3.5" />}
                  onClick={() => {
                    setUnlocking(line);
                    setReason('');
                  }}
                  data-testid={`unlock-ts-${line.timesheetId}`}
                >
                  Unlock
                </Button>
              </Can>
            ) : (
              <Button
                key={line.timesheetId}
                size="xs"
                variant="secondary"
                leadingIcon={<Lock className="h-3.5 w-3.5" />}
                isLoading={lockOne.isPending}
                onClick={() =>
                  lockOne.mutate(
                    { id: line.timesheetId },
                    {
                      onSuccess: (result) => toast.success(result.message ?? 'Locked.'),
                      onError: (error) => toast.fromError(error, 'That timesheet was not locked'),
                    },
                  )
                }
                data-testid={`lock-ts-${line.timesheetId}`}
              >
                Lock
              </Button>
            ),
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Payroll lock"
        description="Locking makes these hours immutable inputs to payroll and billing. Do it once the period is approved and before the payroll run is priced."
        breadcrumbs={[{ label: 'Timesheets', to: '/timesheets' }, { label: 'Payroll lock' }]}
        actions={
          <>
            <Select
              aria-label="Pay period"
              value={String(selectedIndex)}
              onChange={(event) => {
                setSelectedIndex(Number(event.target.value));
                setSkipped([]);
              }}
              className="w-[220px]"
              data-testid="lock-period-select"
            >
              {options.map((entry, index) => (
                <option key={entry.period.startDate} value={index}>
                  {entry.period.label}
                </option>
              ))}
            </Select>
            <Button
              variant="primary"
              leadingIcon={<Lock className="h-3.5 w-3.5" />}
              disabled={period === null || unlockedCount === 0}
              isLoading={lockPeriod.isPending}
              data-testid="lock-whole-period"
              onClick={() => {
                if (period === null) return;
                lockPeriod.mutate(
                  { periodStart, periodEnd },
                  {
                    onSuccess: (result) => {
                      // The skipped list is the point of the response. It is held on screen
                      // rather than in a toast that disappears while the reader is still
                      // deciding whom to chase.
                      setSkipped(result.skipped ?? []);
                      toast.success(result.message ?? 'Period locked.');
                    },
                    onError: (error) => toast.fromError(error, 'The period was not locked'),
                  },
                );
              }}
            >
              Lock the period
            </Button>
          </>
        }
      />

      {skipped.length > 0 && (
        <Banner
          tone="warning"
          className="mb-3"
          title={`${skipped.length} sheet${skipped.length === 1 ? '' : 's'} will not be paid in this run`}
          data-testid="lock-skipped"
        >
          <ul className="list-disc space-y-0.5 pl-4">
            {skipped.map((entry) => (
              <li key={entry.timesheetId}>
                <code className="code-ref">{entry.timesheetId}</code>
                {entry.reason === undefined ? ' was not approved' : `: ${entry.reason}`}
              </li>
            ))}
          </ul>
          Chase these before the run closes — a sheet that is not locked is not in the run.
        </Banner>
      )}

      {rows.some((row) => row.anyExpiredWorkAuth) && (
        <Banner tone="info" className="mb-3">
          Some hours here were entered by somebody whose work authorisation had lapsed. That is deliberate —
          time entry is never blocked on immigration status, because the hours were worked and the obligation
          to pay for them stands. Each one is recorded as an audit event for the compliance review.
        </Banner>
      )}

      {hours.isLoading ? (
        <LoadingRows rows={6} columns={6} />
      ) : hours.isError ? (
        <ErrorState
          error={hours.error}
          onRetry={() => {
            void hours.refetch();
          }}
        />
      ) : (
        <div className="surface-card">
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(row) => row.employeeId}
            testIdPrefix="payroll-lock-row"
            emptyState={
              <EmptyState
                title="Nothing to lock in this period"
                description="No approved timesheets fall inside these dates. If you are expecting some, they may still be awaiting approval — the approvals queue shows what is outstanding."
              />
            }
          />
        </div>
      )}

      <Modal
        open={unlocking !== null}
        onClose={() => {
          setUnlocking(null);
          setReason('');
        }}
        title="Reopen a locked timesheet"
        description={
          unlocking === null ? undefined : formatDateRange(unlocking.periodStart, unlocking.periodEnd)
        }
        size="sm"
        data-testid="unlock-modal"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setUnlocking(null);
                setReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="dangerGhost"
              leadingIcon={<LockOpen className="h-3.5 w-3.5" />}
              disabled={reason.trim().length < 5}
              isLoading={unlockOne.isPending}
              data-testid="confirm-unlock"
              onClick={() => {
                if (unlocking === null) return;
                unlockOne.mutate(
                  { id: unlocking.timesheetId, reason: reason.trim() },
                  {
                    onSuccess: (result) => {
                      // WORKFLOW means the sheet is still locked. Saying "unlocked" here
                      // would send somebody off to edit hours they cannot edit.
                      toast.success(
                        result.message ??
                          (result.route === 'WORKFLOW'
                            ? 'Sent for approval. The timesheet stays locked until it is approved.'
                            : 'Unlocked and returned to approved.'),
                      );
                      setUnlocking(null);
                      setReason('');
                    },
                    onError: (error) => toast.fromError(error, 'The timesheet was not reopened'),
                  },
                );
              }}
            >
              Reopen it
            </Button>
          </>
        }
      >
        <Banner tone="warning" className="mb-3">
          These hours may already have been paid or invoiced. The reason, and both sets of figures, go into
          the audit log at high severity.
        </Banner>
        <FieldShell label="Why is this being reopened?" htmlFor="unlock-reason" required fieldKey="unlock-reason">
          <Textarea
            id="unlock-reason"
            name="unlockReason"
            rawName
            rows={3}
            value={reason}
            autoFocus
            placeholder="Name what is wrong — e.g. 'Thursday was booked to ACME-001; the assignment moved to ACME-004 on the 3rd.'"
            onChange={(event) => setReason(event.target.value)}
            data-testid="unlock-reason"
          />
        </FieldShell>
      </Modal>
    </div>
  );
}
